import {
  buildViewerSourceFromSearchSource,
  openSearchBlockSource,
} from '@heidi/evidence';
import type { Source } from '@heidi/evidence/contracts';
import { cn, useIsMobile } from '@heidi/utils';
import { useCallback, type AriaRole, type ComponentProps, type ReactNode } from 'react';

import { useIsWorkSourceCitationChipsEnabled } from '../../../flags';
import { getWorkHostPort } from '../../work-home/work-home-ports';
import {
  createProjectItemRenderer,
  type ProjectItemRendererDeps,
} from '../timeline/renderProjectItem';

import type { ProjectConversationTimelineModel } from '../hooks/useProjectConversationTimeline';
import type { ProjectItemRendererHooks } from '../hooks/useProjectItemRenderer';
import { PlanProgressDock } from '../timeline/agent-todo/PlanProgressDock';
import { ProjectChatScrollFades } from './ProjectChatScrollFades';
import { ProjectChatSkeleton } from './ProjectChatSkeleton';
import { ProjectScrollToBottomButton } from './ProjectScrollToBottomButton';
import {
  ProjectTimelineView,
  type ProjectTimelineLayoutOptions,
  type ProjectTimelineViewProps,
} from './ProjectTimelineView';

interface ProjectConversationTimelineProps {
  contentClassName?: string;
  contentTestId?: string;
  emptyFallback?: ReactNode;
  initialLoadingFallback?: ReactNode;
  /** Rendered at the top of the transcript scroll, above the timeline (and
   * above the loading/empty fallbacks) — embedders use it to carry an opening
   * assistant turn into the chat (e.g. the routine walkthrough's intro), so
   * the transcript reads as one continuous conversation. */
  leadingContent?: ReactNode;
  model: ProjectConversationTimelineModel;
  /** Per earlier turn, e.g. its response action row. */
  renderCompletedTurnFooter?: ProjectTimelineLayoutOptions['renderCompletedTurnFooter'];
  scrollClassName?: string;
  scrollAriaLive?: 'off' | 'assertive' | 'polite';
  scrollContentClassName?: string;
  scrollInteractive?: boolean;
  scrollRole?: AriaRole;
  scrollTestId?: string;
  /** Pointer down on transcript scroll area — suppresses composer refocus after send. */
  onScrollPointerDown?: (event: React.PointerEvent<HTMLElement>) => void;
  showScrollFades?: boolean;
  /** The jump-to-bottom affordance over the transcript. On by default: the
   * transcript holds its position while a reply streams in below the fold, so
   * this is the way down to it. */
  showScrollToBottomButton?: boolean;
  /** Taller/softer top fade override, forwarded to the scroll fades. */
  topFadeClassName?: string;
  /** Both fades' color override, forwarded to the scroll fades. */
  fadeColorClassName?: string;
  timelineProps: Omit<
    ProjectTimelineViewProps,
    'items' | 'latestTodoPlan' | 'layout' | 'itemRendererHooks'
  >;
  wrapperClassName?: string;
  /** What the item renderer reads from the app, bound once at the composition point. */
  itemRendererHooks: ProjectItemRendererHooks;
  /** The app shell's inset layout, resolved by the app: the scroll fades follow it. */
  insetDesign: boolean;
}

export const ProjectConversationTimelineView = ({
  contentClassName,
  contentTestId,
  emptyFallback,
  initialLoadingFallback = <ProjectChatSkeleton />,
  leadingContent,
  model,
  renderCompletedTurnFooter,
  scrollClassName,
  scrollAriaLive,
  scrollContentClassName,
  scrollInteractive,
  scrollRole,
  scrollTestId,
  onScrollPointerDown,
  showScrollFades = true,
  showScrollToBottomButton = true,
  topFadeClassName,
  fadeColorClassName,
  timelineProps,
  wrapperClassName,
  itemRendererHooks,
  insetDesign,
}: ProjectConversationTimelineProps) => {
  return (
    <div className={cn('relative flex min-h-0 flex-1 flex-col', wrapperClassName)}>
      <div
        ref={model.scrollRef}
        className={scrollClassName}
        data-overlay-interactive={scrollInteractive ? 'true' : undefined}
        data-testid={scrollTestId}
        role={scrollRole}
        aria-live={scrollAriaLive}
        onPointerDown={onScrollPointerDown}
      >
        {/* Inline style, not a Tailwind class: the padding is a runtime number
          shared with the latest-turn floor math in the hook — a class would
          re-introduce the dual source that had to stay manually in lockstep. */}
        <div
          ref={model.scrollContentRef}
          className={scrollContentClassName}
          style={{ paddingBottom: model.scrollContentBottomPaddingPx }}
        >
          {leadingContent}
          {model.isInitialLoading ? (
            initialLoadingFallback
          ) : model.isEmpty ? (
            emptyFallback
          ) : (
            <div
              ref={model.contentRef}
              className={cn('flex min-w-0 flex-col gap-4', contentClassName)}
              data-testid={contentTestId}
            >
              <ProjectTimelineView
                {...timelineProps}
                itemRendererHooks={itemRendererHooks}
                items={model.displayItems}
                latestTodoPlan={model.latestTodoPlan}
                rendererOptions={{
                  ...timelineProps.rendererOptions,
                  shouldAnimateUserMessage: model.shouldAnimateUserMessage,
                }}
                layout={{
                  latestTurnMinHeight: model.latestTurnFloor,
                  tailRef: model.followTailRef,
                  tailContent: model.tailContent,
                  renderCompletedTurnFooter,
                }}
              />
            </div>
          )}
        </div>
      </div>
      {showScrollFades ? (
        <ProjectChatScrollFades
          bottomFadeRef={model.bottomFadeRef}
          scrollRef={model.scrollRef}
          insetDesign={insetDesign}
          topFadeClassName={topFadeClassName}
          fadeColorClassName={fadeColorClassName}
        />
      ) : null}
      {showScrollToBottomButton ? (
        <ProjectScrollToBottomButton
          isVisible={model.hasMoreContentBelow}
          onClick={model.scrollToBottom}
        />
      ) : null}
      {model.showPlanProgressDock ? (
        <PlanProgressDock
          plan={model.latestTodoPlan}
          scrollRef={model.scrollRef}
          isRunActive={model.turnInFlight}
          projectTitle={timelineProps.rendererOptions?.projectTitle}
        />
      ) : null}
    </div>
  );
};

const useOpenEvidenceSource = () => {
  const sourceOpeners = getWorkHostPort('evidence').useEvidenceSourceOpeners();
  const isMobile = useIsMobile();
  return useCallback(
    (source: Source, citationNumber: number) =>
      openSearchBlockSource(
        {
          source,
          isMobile,
          isAuthenticatedUrl: sourceOpeners.isAuthenticatedUrl,
          citationNumber,
          surfaceBlock: 'collapsed_sources_summary',
          sessionId: null,
        },
        {
          trackSourceClick: (props) =>
            sourceOpeners.trackSourceClick({
              ...props,
              collections: undefined,
              ...(source.type === 'user_document' && { source_title: undefined }),
            }),
          openLibraryDocument: sourceOpeners.openLibraryDocument,
          openInViewer: (src) =>
            sourceOpeners.openInViewer(buildViewerSourceFromSearchSource(src)),
          openInNewTab: sourceOpeners.openInNewTab,
        }
      ),
    [sourceOpeners, isMobile]
  );
};

const ITEM_RENDERER_HOOKS: ProjectItemRendererHooks = {
  useCreateItemRenderer: () => {
    const openEvidenceSource = useOpenEvidenceSource();
    return useCallback(
      (deps: ProjectItemRendererDeps) =>
        createProjectItemRenderer({ ...deps, openEvidenceSource }),
      [openEvidenceSource]
    );
  },
  useIsNangoIntegrationsEnabled: () =>
    getWorkHostPort('shell').useIsNangoIntegrationsEnabled(),
  useIsWorkSourceCitationChipsEnabled,
  get desktopControl() {
    return getWorkHostPort('desktop').desktopControlApprovalPort;
  },
};

type ProjectConversationTimelineBoundProps = Omit<
  ComponentProps<typeof ProjectConversationTimelineView>,
  'itemRendererHooks' | 'insetDesign'
>;

/** The Work transcript, bound to the item renderer and the inset flag. */
export const ProjectConversationTimeline = (
  props: ProjectConversationTimelineBoundProps
) => (
  <ProjectConversationTimelineView
    {...props}
    itemRendererHooks={ITEM_RENDERER_HOOKS}
    insetDesign={
      props.showScrollFades !== false &&
      getWorkHostPort('shell').IS_INSET_DESIGN_ENABLED()
    }
  />
);
